import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";

import McpKeyList from "../../components/Mcp/McpKeyList";
import McpCreateKeyDialog from "../../dialogs/McpCreateKeyDialog";
import McpKeyCreatedDialog from "../../dialogs/McpKeyCreatedDialog";
import McpKeyDetailsDialog from "../../dialogs/McpKeyDetailsDialog";
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";

import useMcpApiKeys from "../../utils/hooks/mcp/useMcpApiKeys";
import useMcpKeyDetails from "../../utils/hooks/mcp/useMcpKeyDetails";
import { listMcpToolsApi } from "../../services/api/mcpApi";
import {
  MCP_CREATE_LABELS,
  MCP_CONNECT_STEPS,
  MCP_CREATED_LABELS,
  MCP_DEFAULT_EXPIRY_DAYS,
  MCP_DEFAULT_TOOLS,
  MCP_DELETE_LABELS,
  MCP_DETAILS_LABELS,
  MCP_EXPIRY_OPTIONS,
  MCP_TEXT,
  copyTextToClipboard,
  normalizeToolCatalog,
  toolGroupOf,
} from "../../utils/HelperFunction/mcpHelpers";

const DELETE_DESCRIPTION = (name) =>
  `Delete "${name}"? Any assistant using this key will stop working immediately. This cannot be undone.`;

const defaultCatalog = () =>
  MCP_DEFAULT_TOOLS.map((name) => ({
    name,
    description: "",
    group: toolGroupOf(name),
  }));

export default function McpConnector() {
  const userInfo = useSelector((state) => state.userLogin?.userInfo);
  const userId = userInfo?._id || userInfo?.id || userInfo?.user?._id;

  const { keys, loading, error, saving, reload, createKey, deleteKey } =
    useMcpApiKeys({ userId });

  const [createOpen, setCreateOpen] = useState(false);
  const [catalog, setCatalog] = useState({
    tools: defaultCatalog(),
    loading: false,
    fallback: false,
    loaded: false,
  });
  const [createdKey, setCreatedKey] = useState(null);
  const [detailsKeyId, setDetailsKeyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { details, traces, reloadDetails, reloadTraces } = useMcpKeyDetails({
    userId,
    keyId: detailsKeyId,
  });

  const handleCopy = useCallback(async (text) => {
    const ok = await copyTextToClipboard(text);
    if (ok) toast.success(MCP_TEXT.COPIED);
    else toast.error(MCP_TEXT.COPY_FAILED);
  }, []);

  const openCreate = async () => {
    setCreateOpen(true);
    if (catalog.loaded || !userId) return;

    setCatalog((c) => ({ ...c, loading: true }));
    try {
      const response = await listMcpToolsApi(userId, "full");
      setCatalog({
        tools: normalizeToolCatalog(response.data),
        loading: false,
        fallback: false,
        loaded: true,
      });
    } catch {
      setCatalog({
        tools: defaultCatalog(),
        loading: false,
        fallback: true,
        loaded: true,
      });
    }
  };

  const handleCreate = async (payload) => {
    const created = await createKey(payload);
    if (created) {
      setCreateOpen(false);
      setCreatedKey(created);
    }
  };

  const handleDelete = async () => {
    const ok = await deleteKey(deleteTarget.id);
    if (ok) setDeleteTarget(null);
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        py: { xs: 1, lg: 3 },
        height: "calc(100vh - 50px)",
        width: { xs: "100%", lg: "calc(100% - 45px)" },
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant='h5' fontWeight='bold' sx={{ color: "#003D78" }}>
            {MCP_TEXT.PAGE_TITLE}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {MCP_TEXT.PAGE_SUBTITLE}
          </Typography>
        </Box>

        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={openCreate}
          disabled={!userId}
          sx={{ backgroundColor: "#003D78", textTransform: "none" }}
        >
          {MCP_TEXT.CREATE_BUTTON}
        </Button>
      </Box>

      <Alert severity='warning' sx={{ mb: 2 }}>
        {MCP_TEXT.SECURITY_NOTE}
      </Alert>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button
          size='small'
          startIcon={<RefreshIcon />}
          onClick={reload}
          disabled={loading}
          sx={{ textTransform: "none" }}
        >
          {MCP_TEXT.REFRESH}
        </Button>
      </Box>

      {loading && keys.length === 0 ? (
        <Box
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, py: 6 }}
        >
          <CircularProgress size={28} />
          <Typography variant='body2'>{MCP_TEXT.LOADING}</Typography>
        </Box>
      ) : error ? (
        <Alert
          severity='error'
          action={
            <Button color='inherit' size='small' onClick={reload}>
              {MCP_TEXT.RETRY}
            </Button>
          }
        >
          {error}
        </Alert>
      ) : (
        <McpKeyList
          keys={keys}
          text={MCP_TEXT}
          busy={saving}
          onViewDetails={(key) => setDetailsKeyId(key.id)}
          onDelete={setDeleteTarget}
          onCreate={userId ? openCreate : undefined}
        />
      )}

      <Box sx={{ mt: 3 }}>
        <Typography variant='subtitle1' fontWeight='bold' sx={{ mb: 1 }}>
          {MCP_CONNECT_STEPS.title}
        </Typography>
        <Box component='ol' sx={{ m: 0, pl: 3 }}>
          {MCP_CONNECT_STEPS.steps.map((step) => (
            <Typography component='li' variant='body2' key={step} sx={{ mb: 0.5 }}>
              {step}
            </Typography>
          ))}
        </Box>
      </Box>

      <McpCreateKeyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        submitting={saving}
        labels={MCP_CREATE_LABELS}
        tools={catalog.tools}
        toolsLoading={catalog.loading}
        toolsFallback={catalog.fallback}
        expiryOptions={MCP_EXPIRY_OPTIONS}
        defaultExpiry={MCP_DEFAULT_EXPIRY_DAYS}
      />

      <McpKeyCreatedDialog
        open={Boolean(createdKey)}
        onClose={() => setCreatedKey(null)}
        created={createdKey}
        labels={MCP_CREATED_LABELS}
        onCopy={handleCopy}
      />

      <McpKeyDetailsDialog
        open={Boolean(detailsKeyId)}
        onClose={() => setDetailsKeyId(null)}
        details={details}
        traces={traces}
        onRetryDetails={reloadDetails}
        onRetryTraces={reloadTraces}
        labels={MCP_DETAILS_LABELS}
      />

      <ConfirmationDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={MCP_DELETE_LABELS.title}
        description={deleteTarget ? DELETE_DESCRIPTION(deleteTarget.name) : ""}
        confirmText={MCP_DELETE_LABELS.confirm}
        cancelText={MCP_DELETE_LABELS.cancel}
        loading={saving}
      />
    </Container>
  );
}
